// /app/frontend/src/components/frugal-v2/components/EditItemModal.jsx
// Modal for editing items with dynamic categories (150 lines)

import React, { useState, useEffect } from 'react';
import { X } from 'lucide-react';
import { EXPENSE_CATEGORIES, INCOME_CATEGORIES, SAVING_CATEGORIES, translateCategory } from '../constants/categories';

// Get categories based on type
const getCategoriesByType = (type, customCategories) => {
  if (customCategories) return customCategories;
  switch (type) {
    case 'income': return INCOME_CATEGORIES;
    case 'saving': return SAVING_CATEGORIES;
    case 'expense': return EXPENSE_CATEGORIES;
    default: return EXPENSE_CATEGORIES;
  }
};

const EditItemModal = ({
  item,
  type, // 'income' | 'saving' | 'expense'
  onSave,
  onDelete,
  onClose,
  showCategory = false,
  categories: customCategories,
  t
}) => {
  const categories = getCategoriesByType(type, customCategories);
  const [name, setName] = useState('');
  const [amount, setAmount] = useState('');
  const [day, setDay] = useState('1');
  const [category, setCategory] = useState(categories[0] || 'Otros');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  useEffect(() => {
    if (item) {
      // Support both 'name' (expenses) and 'description' (transactions)
      setName(item.name || item.description || '');
      setAmount(item.amount?.toString() || '');
      setDay(item.day?.toString() || '1');
      setCategory(item.category || categories[0] || 'Otros');
    }
  }, [item, categories]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!name.trim() || !amount) return;

    setIsSubmitting(true);
    try {
      await onSave(item.id, {
        name: name.trim(),
        amount: parseFloat(amount),
        day: parseInt(day),
        category: showCategory ? category : undefined
      });
      onClose();
    } catch (error) {
      console.error('Error saving item:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!item) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4 overflow-y-auto">
      <div className="bg-slate-800 rounded-xl border border-white/10 w-full max-w-md my-8 max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-white/10">
          <h3 className="text-lg font-semibold text-white">
            {t('frugal.actions.edit')}
          </h3>
          <button onClick={onClose} className="text-slate-400 hover:text-white">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-4 space-y-4 pb-6">
          <div>
            <label className="block text-sm text-slate-400 mb-1">{t('frugal.form.description')}</label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="w-full px-3 py-2 bg-white/10 border border-white/20 text-white rounded-md focus:ring-2 focus:ring-emerald-500"
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm text-slate-400 mb-1">{t('frugal.form.day')}</label>
              <input
                type="number"
                min="1"
                max="31"
                value={day}
                onChange={(e) => setDay(e.target.value)}
                className="w-full px-3 py-2 bg-white/10 border border-white/20 text-white rounded-md focus:ring-2 focus:ring-emerald-500"
              />
            </div>
            <div>
              <label className="block text-sm text-slate-400 mb-1">{t('frugal.form.amount')}</label>
              <input
                type="number"
                step="0.01"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                className="w-full px-3 py-2 bg-white/10 border border-white/20 text-white rounded-md focus:ring-2 focus:ring-emerald-500"
              />
            </div>
          </div>

          {showCategory && (
            <div>
              <label className="block text-sm text-slate-400 mb-1">{t('frugal.form.category')}</label>
              <select
                value={category}
                onChange={(e) => setCategory(e.target.value)}
                className="w-full px-3 py-2 bg-white/10 border border-white/20 text-white rounded-md focus:ring-2 focus:ring-emerald-500"
                style={{ maxHeight: '200px' }}
              >
                {categories.map(cat => (
                  <option key={cat} value={cat} className="bg-slate-800">
                    {translateCategory(t, cat)}
                  </option>
                ))}
              </select>
            </div>
          )}

          {/* Actions */}
          <div className="flex gap-3 pt-2">
            {onDelete && (
              <button
                type="button"
                onClick={() => setShowDeleteConfirm(true)}
                className="py-2 px-4 bg-red-500/20 hover:bg-red-500/30 text-red-400 rounded-md transition-colors"
              >
                {t('frugal.actions.delete') || 'Delete'}
              </button>
            )}
            <button
              type="button"
              onClick={onClose}
              className="flex-1 py-2 bg-white/10 hover:bg-white/20 text-slate-400 rounded-md transition-colors"
            >
              {t('frugal.actions.cancel')}
            </button>
            <button
              type="submit"
              disabled={isSubmitting || !name.trim() || !amount}
              className="flex-1 py-2 bg-emerald-500 hover:bg-emerald-600 text-white rounded-md transition-colors disabled:opacity-50"
            >
              {t('frugal.actions.save')}
            </button>
          </div>
          
          {/* Delete Confirmation */}
          {showDeleteConfirm && (
            <div className="mt-4 p-3 bg-red-500/10 border border-red-500/30 rounded-lg">
              <p className="text-sm text-red-400 mb-3">
                {t('frugal.actions.confirmDelete') || '¿Estás seguro de eliminar este elemento?'}
              </p>
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={() => setShowDeleteConfirm(false)}
                  className="flex-1 py-2 bg-white/10 hover:bg-white/20 text-slate-400 rounded-md text-sm"
                >
                  {t('frugal.actions.cancel')}
                </button>
                <button
                  type="button"
                  onClick={async () => {
                    setIsSubmitting(true);
                    try {
                      await onDelete(item.id);
                      onClose();
                    } catch (error) {
                      console.error('Error deleting:', error);
                    } finally {
                      setIsSubmitting(false);
                    }
                  }}
                  disabled={isSubmitting}
                  className="flex-1 py-2 bg-red-500 hover:bg-red-600 text-white rounded-md text-sm disabled:opacity-50"
                >
                  {t('frugal.actions.confirmDeleteBtn') || 'Sí, eliminar'}
                </button>
              </div>
            </div>
          )}
        </form>
      </div>
    </div>
  );
};

export default EditItemModal;
